/**
 * The repair log on a maintenance record (api#979 / 0492): cause, failed
 * component, parts lines, the out-of-service window, and the inspection that
 * found it.
 *
 * Two rules from the API that this file exists to keep:
 *
 *   - parts_cost and downtime_hours are DERIVED BY THE API when the form does
 *     not send them (from the parts lines and the window). So an empty box is
 *     sent as NO KEY — never 0, which the API would keep as a real figure and
 *     derive nothing. Nothing here totals the lines or subtracts the times.
 *   - an EDIT sends `null` only to clear a value the record had; an untouched
 *     empty box sends nothing, which the API reads as "leave it".
 */
import { fromDatetimeLocal } from './datetime-local';

/** The API's vocabulary, operator-approved 2026-10-09, in its order. */
export const REPAIR_CAUSES = [
  'preventive',
  'inspection_finding',
  'wear',
  'damage',
  'failure_in_service',
  'operator_report',
  'other',
] as const;
export type RepairCause = (typeof REPAIR_CAUSES)[number];

/** Exhaustive by type, so a new API value is a compile error here, not a raw key on screen. */
export const REPAIR_CAUSE_LABELS: Record<RepairCause, string> = {
  preventive: 'Preventive',
  inspection_finding: 'Inspection finding',
  wear: 'Wear',
  damage: 'Damage',
  failure_in_service: 'Failure in service',
  operator_report: 'Operator report',
  other: 'Other',
};

/** A parts line as typed — all strings, so an empty box stays empty. */
export interface PartDraft {
  part: string;
  part_number: string;
  qty: string;
  unit_cost: string;
}

export const emptyPart = (): PartDraft => ({ part: '', part_number: '', qty: '', unit_cost: '' });

const blankLine = (p: PartDraft) => !p.part.trim() && !p.part_number.trim() && !p.qty.trim() && !p.unit_cost.trim();

/** Stored lines (the record's `parts` jsonb) → drafts. */
export function partsToDrafts(parts: unknown): PartDraft[] {
  if (!Array.isArray(parts)) return [];
  return parts.map((p: any) => ({
    part: typeof p?.part === 'string' ? p.part : '',
    part_number: typeof p?.part_number === 'string' ? p.part_number : '',
    qty: p?.qty != null ? String(p.qty) : '',
    unit_cost: p?.unit_cost != null ? String(p.unit_cost) : '',
  }));
}

export interface PartLine { part: string; part_number?: string; qty: number; unit_cost?: number }

/** The first problem with the lines, worded for the office, or null. Mirrors the API's 400s. */
export function partsError(drafts: PartDraft[]): string | null {
  for (let i = 0; i < drafts.length; i++) {
    const p = drafts[i];
    if (blankLine(p)) continue;
    const n = i + 1;
    if (!p.part.trim()) return `Part line ${n}: name the part`;
    const qty = Number(p.qty);
    if (!p.qty.trim() || !Number.isFinite(qty) || qty <= 0) return `Part line ${n}: quantity must be more than 0`;
    if (p.unit_cost.trim()) {
      const uc = Number(p.unit_cost);
      if (!Number.isFinite(uc) || uc < 0) return `Part line ${n}: unit cost must be 0 or more`;
    }
  }
  return null;
}

/** Drafts → the wire array. Wholly blank lines are dropped. Call partsError first. */
export function serializeParts(drafts: PartDraft[]): PartLine[] {
  return drafts.filter((p) => !blankLine(p)).map((p) => {
    const line: PartLine = { part: p.part.trim(), qty: Number(p.qty) };
    if (p.part_number.trim()) line.part_number = p.part_number.trim();
    if (p.unit_cost.trim()) line.unit_cost = Number(p.unit_cost);
    return line;
  });
}

/**
 * The value to send for an optional field, or `undefined` for "no key".
 *
 *   typed           → the value
 *   empty, and the record HAD a value (an edit clearing it) → null
 *   empty otherwise → undefined (no key: the API derives or leaves it)
 */
export function optionalField<T>(value: T | null, hadValue: boolean): T | null | undefined {
  if (value !== null) return value;
  return hadValue ? null : undefined;
}

/** A datetime-local picker string → the ISO instant on the tenant's clock, or null when empty. */
export function windowInstant(local: string, timeZone: string | null): string | null {
  return local.trim() ? fromDatetimeLocal(local, timeZone) : null;
}
